import React, { useState, useId } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  FileDownloadOutlined as DownloadIcon,
  DescriptionOutlined as WordIcon,
  PictureAsPdfOutlined as PdfIcon,
  CodeOutlined as HtmlIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import enhancedExportService from '../services/enhancedExportService';

const EXPORT_OPTIONS = [
  { type: 'word', label: 'Word', hint: '.docx', icon: <WordIcon fontSize="small" /> },
  { type: 'pdf', label: 'PDF', hint: '.pdf', icon: <PdfIcon fontSize="small" /> },
  { type: 'html', label: 'HTML', hint: '.html', icon: <HtmlIcon fontSize="small" /> },
];

/**
 * Export menu for a list of requirements (Word, PDF or HTML).
 *
 * @param {object} props
 * @param {object[]} props.requirements - Requirements to export.
 * @param {string} props.projectName - Used for the document title and file name.
 * @param {'contained'|'outlined'|'text'} [props.variant='contained'] - Button variant.
 * @param {'small'|'medium'|'large'} [props.size='medium'] - Button size.
 * @param {boolean} [props.disabled=false] - Disable the button.
 * @param {string} [props.buttonText='Export Requirements'] - Button label.
 * @param {object} [props.sx] - Button styles.
 * @returns {JSX.Element}
 */
const RequirementsExportDropdown = ({
  requirements,
  projectName,
  variant = 'contained',
  size = 'medium',
  disabled = false,
  buttonText = 'Export Requirements',
  sx = {},
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportType, setExportType] = useState('');
  const [error, setError] = useState('');
  const baseId = useId();
  const buttonId = `${baseId}-button`;
  const menuId = `${baseId}-menu`;
  const open = Boolean(anchorEl);
  const count = requirements?.length || 0;

  const handleExport = async (type) => {
    if (!count) return;

    setExporting(true);
    setExportType(type);
    setAnchorEl(null);

    try {
      const baseFileName = `${projectName}-requirements`.replace(/[^a-z0-9]/gi, '_').toLowerCase();

      switch (type) {
        case 'word':
          await enhancedExportService.exportRequirements(requirements, projectName, `${baseFileName}.docx`);
          break;
        case 'pdf':
          await enhancedExportService.exportRequirementsToPDF(requirements, projectName, `${baseFileName}.pdf`);
          break;
        case 'html':
          await enhancedExportService.exportRequirementsToHTML(requirements, projectName, `${baseFileName}.html`);
          break;
        default:
          throw new Error('Unknown export type');
      }
    } catch (err) {
      console.error(`Requirements ${type.toUpperCase()} export failed:`, err);
      setError(`Export failed: ${err.message || 'unknown error'}`);
    } finally {
      setExporting(false);
      setExportType('');
    }
  };

  return (
    <>
      <Button
        id={buttonId}
        variant={variant}
        size={size}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        disabled={disabled || exporting || !count}
        startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon fontSize="small" />}
        endIcon={exporting ? null : <ArrowDownIcon fontSize="small" />}
        sx={sx}
        aria-controls={open ? menuId : undefined}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : undefined}
      >
        {exporting ? `Exporting ${exportType.toUpperCase()}...` : buttonText}
      </Button>

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ 'aria-labelledby': buttonId }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {EXPORT_OPTIONS.map((option) => (
          <MenuItem key={option.type} onClick={() => handleExport(option.type)}>
            <ListItemIcon>{option.icon}</ListItemIcon>
            <ListItemText
              primary={`${option.label} (${option.hint})`}
              secondary={`${count} requirement${count === 1 ? '' : 's'}`}
            />
          </MenuItem>
        ))}
      </Menu>

      <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default RequirementsExportDropdown;
